import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getSettingsPageUrl } from '@constants'
import { useBusinessContext, useFeature } from '@root/context'
import { IInvoice } from '@root/query'
import { postDueAutomationInactiveState, shouldShowPostDueAutomationDetailAlert } from './postDueUi'
import {
  PostDueAutomationAlertDescriptionStack,
  PostDueAutomationAlertLayout,
  PostDueAutomationAlertLineStack
} from './PostDueAutomationAlertLayout'
import styled from 'styled-components'

interface Props {
  invoice: IInvoice
}

export const PostDueAutomationInactiveAlert: React.FC<Props> = ({ invoice }) => {
  const { slug, data: business } = useBusinessContext()
  const { enabled: kraviaEnabled } = useFeature('kravia')
  const [t] = useTranslation()

  if (!shouldShowPostDueAutomationDetailAlert(invoice, business)) {
    return null
  }

  const { reminderInactive, collectionInactive } = postDueAutomationInactiveState(
    invoice,
    business,
    kraviaEnabled
  )
  if (!reminderInactive && !collectionInactive) {
    return null
  }

  const lines: string[] = []
  if (reminderInactive) {
    lines.push(t('invoicing.details.postDueAutomationInactive.reminderReason'))
  }
  if (collectionInactive) {
    lines.push(t('invoicing.details.postDueAutomationInactive.collectionReason'))
  }

  const settingsUrl = getSettingsPageUrl(slug, { page: 'invoicing-debt-collection' })
  const alertTitle =
    reminderInactive && collectionInactive
      ? t('invoicing.details.postDueAutomationInactive.titleBoth')
      : reminderInactive
      ? t('invoicing.details.postDueAutomationInactive.titleReminderOnly')
      : t('invoicing.details.postDueAutomationInactive.titleCollectionOnly')

  return (
    <PostDueAutomationAlertLayout
      type="warning"
      title={alertTitle}
      description={
        <PostDueAutomationAlertDescriptionStack>
          <PostDueAutomationAlertLineStack>
            {lines.map(line => (
              <div key={line}>{line}</div>
            ))}
          </PostDueAutomationAlertLineStack>
          <SettingsLink to={settingsUrl}>
            {t('invoicing.details.postDueAutomationInactive.openSettings')}
          </SettingsLink>
        </PostDueAutomationAlertDescriptionStack>
      }
    />
  )
}

const SettingsLink = styled(Link)`
  margin-top: ${({ theme }) => theme.spacing.sm}rem;
  align-self: flex-start;
`
